'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import SectionHeading from '../layout/section-heading';

/**
 * ✅ How to use your child icons:
 * Put transparent PNG/SVG icons in /public/child-icons/
 * Example files:
 *  - /public/child-icons/star.svg
 *  - /public/child-icons/book.svg
 *  - /public/child-icons/pencil.svg
 *  - /public/child-icons/rocket.svg
 *
 * Then update `floatingIcons` below with your real paths.
 */

const categoriesData = [
    {
        title: 'Spark a Lifelong Reading Habit',
        description:
            'Reading opens doors to imagination and learning. Our services help authors create engaging books that children will enjoy again and again, sparking their curiosity and love for stories through high-quality self-publishing guidance.',
        image: '/child-ebook/spark-a-life-long.png',
        // optional: each card can have a slightly different background accent
        accent: 'from-pink-500/25 via-purple-500/15 to-blue-500/20',
    },
    {
        title: 'Encourage Creative Exploration',
        description:
            'Children’s books are more than words on a page—they inspire creativity. We help authors craft tales that encourage kids to dream, imagine, and explore new worlds with our end-to-end self-publishing assistance.',
        image: '/child-ebook/encourage-creative-exploration.png',
        accent: 'from-amber-500/25 via-orange-500/15 to-red-500/20',
    },
    {
        title: 'Build a Habit of Reading',
        description:
            'A great story can shape a young mind. Our publishing support ensures your book is accessible, captivating, and memorable, helping children develop a love for reading that lasts a lifetime.',
        image: '/child-ebook/build-a-habbit-of-reading.png',
        accent: 'from-emerald-500/25 via-teal-500/15 to-cyan-500/20',
    },
    {
        title: 'Making Every Story an Adventure',
        description:
            'Books should be entertaining and educational. We help authors create playful, imaginative stories that children enjoy, making reading a fun and exciting activity through our tailored children’s book self-publishing services.',
        image: '/child-ebook/making-every-story-a-adventure.png',
        accent: 'from-blue-500/25 via-indigo-500/15 to-purple-500/20',
    },
];

// ✅ Replace these with YOUR icon assets
const floatingIcons = [
    { src: '/child-icons/star.svg', className: 'top-6 left-6 w-10 rotate-[-12deg]' },
    { src: '/child-icons/book.svg', className: 'top-10 right-10 w-12 rotate-[10deg]' },
    { src: '/child-icons/pencil.svg', className: 'bottom-10 left-10 w-12 rotate-[14deg]' },
    { src: '/child-icons/rocket.svg', className: 'bottom-8 right-8 w-11 rotate-[-8deg]' },
];

function FloatingIconsLayer() {
    return (
        <div className="pointer-events-none absolute inset-0 z-10">
            {/* soft blobs */}
            <div className="absolute -top-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-12 -right-12 h-44 w-44 rounded-full bg-white/10 blur-3xl" />

            {/* floating icons */}
            {floatingIcons.map((ic, idx) => (
                <img
                    key={idx}
                    src={ic.src}
                    alt=""
                    aria-hidden="true"
                    className={cn(
                        'absolute opacity-25 mix-blend-overlay drop-shadow-sm',
                        'transition-transform duration-700 ease-out',
                        // subtle drift on hover
                        'group-hover:translate-y-[-6px] group-hover:scale-105',
                        ic.className
                    )}
                    loading="lazy"
                />
            ))}

            {/* dotted pattern overlay */}
            <div
                className="absolute inset-0 opacity-[0.12]"
                style={{
                    backgroundImage:
                        'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.55) 1px, transparent 0)',
                    backgroundSize: '18px 18px',
                }}
            />
        </div>
    );
}

function CategoryHoverCard({ title, description, image, accent }) {
    return (
        <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/40 shadow-xl">
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={image}
                    alt={title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                />

                {/* Base gradient for readability */}
                <div className="absolute inset-0 bg-linear-to-t from-black/75 via-black/25 to-black/10" />

                {/* Color accent wash (makes each card feel unique) */}
                <div className={cn('absolute inset-0 bg-linear-to-br', accent)} />
            </div>

            {/* Creative icon layer (only decorative) */}
            <FloatingIconsLayer />

            {/* Content */}
            <div className="relative z-20 flex min-h-[350px] flex-col justify-end p-6">
                {/* little badge */}
                <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs text-white backdrop-blur-md">
                    <span className="inline-block h-2 w-2 rounded-full bg-white/80" />
                    Children’s Publishing
                </div>

                <h3 className="fs-30 font-semibold text-white drop-shadow">{title}</h3>

                {/* Hover content */}
                <div className="mt-3 max-h-0 overflow-hidden opacity-0 transition-all duration-500 max-md:max-h-none max-md:h-auto max-md:opacity-100 group-hover:max-h-48 group-hover:opacity-100">
                    <p className="leading-relaxed text-white">{description}</p>
                </div>

                {/* “read more” hint */}
                <div className="mt-4 inline-flex w-fit items-center gap-2 text-sm font-semibold text-white opacity-90 transition group-hover:opacity-100">
                    <span className="h-7 w-7 rounded-full bg-white/15 grid place-items-center">
                        +
                    </span>
                    <span className="underline-offset-4 group-hover:underline">Hover to read</span>
                </div>

                {/* Hover overlay tint */}
                <div className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-black/35" />
            </div>
        </div>
    );
}

export default function ChildrenCategoriesSection({ className, title, description }) {
    const items = useMemo(() => categoriesData, []);

    return (
        <section className={cn(className)}>
            <div className="container mx-auto">
                <SectionHeading
                    titleClassName="mx-auto mb-6 max-w-6xl"
                    descriptionClassName="max-w-6xl"
                    align="center"
                    title={title}
                    description={description}
                />

                <div className="container max-w-6xl mt-14">
                    {/* Cards grid */}
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2">
                        {items.map((item) => (
                            <CategoryHoverCard key={item.title} {...item} />
                        ))}
                    </div>

                    {/* Mobile helper text */}
                    <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400 lg:hidden">
                        Tip: Tap a card to read more.
                    </p>
                </div>
            </div>
        </section>
    );
}
